import React, { useEffect, useState } from "react";
import { Subheading } from "../../../../design/heading";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../../design/table";
import { Badge } from "../../../../design/badge";
import moment from "moment";
import { EventService } from "../../../../services/EventService";
import { useAccount } from "../../../../contexts/AccountContext";
import {
  Event,
  PHONE_CALL_DIRECTION_LABELS,
  PHONE_CALL_PARTY_LABELS,
} from "@visitproof/types";
import LoadingView from "../../../../components/LoadingView";

interface Props {
  caseId: string;
}

/**
 * Phone calls logged for this case. Includes both calls logged during a visit
 * and calls logged from the case screen (which have no visitId and so never
 * appear in any per-visit event list).
 */
const CaseDetailPhoneCallList: React.FC<Props> = ({ caseId }) => {
  const { account } = useAccount();
  const [calls, setCalls] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCalls = async () => {
      if (!account?.id) return;

      setLoading(true);
      try {
        const fetched = await EventService.getPhoneCallsByCase(caseId, account.id);
        setCalls(fetched);
      } catch (error) {
        console.error("Error fetching phone calls:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchCalls();
  }, [caseId, account?.id]);

  if (loading) {
    return <LoadingView />;
  }

  return (
    <>
      <div className="flex flex-wrap items-center justify-between w-full">
        <Subheading>Phone Calls</Subheading>
      </div>
      {calls.length === 0 ? (
        <div className="py-8 text-center">
          <p className="text-sm text-zinc-500 dark:text-zinc-400">
            No phone calls have been logged for this case yet.
          </p>
        </div>
      ) : (
        <Table className="mt-4">
          <TableHead>
            <TableRow>
              <TableHeader>Date</TableHeader>
              <TableHeader>Direction</TableHeader>
              <TableHeader>With</TableHeader>
              <TableHeader>Notes</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {calls.map((call) => (
              <TableRow key={call.id}>
                <TableCell className="whitespace-nowrap">
                  {call.createdAt
                    ? moment(
                        // Firestore Timestamp or ISO string
                        typeof (call.createdAt as any)?.toDate === "function"
                          ? (call.createdAt as any).toDate()
                          : (call.createdAt as any)
                      ).format("MMM D, YYYY h:mm A")
                    : "—"}
                </TableCell>
                <TableCell>
                  {call.callDirection ? (
                    <Badge color={call.callDirection === "incoming" ? "sky" : "teal"}>
                      {PHONE_CALL_DIRECTION_LABELS[call.callDirection]}
                    </Badge>
                  ) : (
                    "—"
                  )}
                </TableCell>
                <TableCell>
                  {call.callWith ? PHONE_CALL_PARTY_LABELS[call.callWith] : "—"}
                </TableCell>
                <TableCell className="max-w-md">
                  <span className="text-zinc-600 dark:text-zinc-300">
                    {call.body || "—"}
                  </span>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </>
  );
};

export default CaseDetailPhoneCallList;
